import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { Layout } from '../components/layout/Layout';
import { performanceApi } from '../lib/api';
import type { TradeExecution, Position, ClosedPnL, AggregateMetrics, UserPerformanceData } from '../lib/types';
import { DateRangePickerNew as DateRangePicker } from '../components/ui/DateRangePickerNew';
import { RefreshCw, Download } from 'lucide-react';
import { exportTradingDataToExcel } from '../utils/excelExport';
import { AggregateMetricsSection } from '../components/performance/AggregateMetricsSection';
import { TraderPerformanceSummary } from '../components/performance/TraderPerformanceSummary';
import { TradingFeesSummary } from '../components/performance/TradingFeesSummary';
import {
  PerformanceChartSection,
  TradeChartPoint,
  TraderLineConfig,
  TraderColorConfig,
} from '../components/performance/PerformanceChartSection';
import { CurrentPositionsSection } from '../components/performance/CurrentPositionsSection';
import { CoinPerformanceSummary, SymbolPerformanceStat } from '../components/performance/CoinPerformanceSummary';
import { ClosedTradesTable } from '../components/performance/ClosedTradesTable';
import { TradeExecutionsTable } from '../components/performance/TradeExecutionsTable';
import { LoadingSkeleton } from '../components/performance/LoadingSkeleton';

const SHOW_ALL_TRADE_EXECUTIONS = false;

const EMPTY_AGGREGATE: AggregateMetrics = {
  totalUsers: 0,
  totalTrades: 0,
  totalPnl: 0,
  totalVolume: 0,
  averageWinRate: 0,
  totalTimeInMarket: 0,
};

const EMPTY_USERS: UserPerformanceData[] = [];

const TRADER_COLOR_PALETTE: TraderColorConfig[] = [
  { line: '#2563eb', profitBar: '#a7f3d0', lossBar: '#fecaca' },
  { line: '#d946ef', profitBar: '#86efac', lossBar: '#fca5a5' },
  { line: '#0ea5e9', profitBar: '#6ee7b7', lossBar: '#f87171' },
  { line: '#f97316', profitBar: '#4ade80', lossBar: '#fb7185' },
  { line: '#9333ea', profitBar: '#34d399', lossBar: '#f472b6' },
  { line: '#14b8a6', profitBar: '#22c55e', lossBar: '#ef4444' },
  { line: '#8b5cf6', profitBar: '#10b981', lossBar: '#dc2626' },
];

type ExtendedClosedPnL = ClosedPnL & { userName: string; openingFee?: string; closingFee?: string };
type ExtendedTradeExecution = TradeExecution & { userName: string };
type ExtendedPosition = Position & { userName: string };

export const Performance: React.FC = () => {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  const { data: performanceData, isLoading, error, refetch } = useQuery({
    queryKey: ['performance', dateRange],
    queryFn: async () => {
      const response = await performanceApi.getPerformanceData({
        startDate: dateRange?.from?.toISOString(),
        endDate: dateRange?.to?.toISOString(),
      });
      return response.data;
    },
    refetchInterval: 30000,
    enabled: !!dateRange?.from && !!dateRange?.to,
  });

  const users = performanceData?.users ?? EMPTY_USERS;
  const aggregate = performanceData?.aggregate ?? EMPTY_AGGREGATE;
  const timestampLabel = performanceData ? format(new Date(performanceData.timestamp), 'dd/MM/yyyy HH:mm') : 'Loading...';
  const note = performanceData?.note ?? 'No performance note available.';

  const allClosedPnL: ExtendedClosedPnL[] = useMemo(
    () =>
      users.flatMap((user) =>
        user.closedPnL.map((pnl) => {
          const legacyFees = pnl as { openFee?: string; closeFee?: string };
          return {
            ...pnl,
            userName: user.userName,
            openingFee: pnl.openingFee ?? legacyFees.openFee,
            closingFee: pnl.closingFee ?? legacyFees.closeFee,
          };
        }),
      ),
    [users],
  );

  const traderLineConfigs: TraderLineConfig[] = useMemo(
    () =>
      users.map((user, index) => ({
        userId: user.userId,
        userName: user.userName,
        colors: TRADER_COLOR_PALETTE[index % TRADER_COLOR_PALETTE.length],
      })),
    [users],
  );

  const allTrades: ExtendedTradeExecution[] = useMemo(
    () => users.flatMap((user) => user.trades.map((trade) => ({ ...trade, userName: user.userName }))),
    [users],
  );

  const allPositions: ExtendedPosition[] = useMemo(
    () =>
      users.flatMap((user) =>
        user.positions
          .filter((position) => Number.parseFloat(position.size) > 0)
          .map((position) => ({ ...position, userName: user.userName })),
      ),
    [users],
  );

  const chartData: TradeChartPoint[] = useMemo(() => {
    if (allClosedPnL.length === 0) {
      return [];
    }

    const sortedTrades = [...allClosedPnL].sort(
      (a, b) => Number.parseInt(a.updatedTime, 10) - Number.parseInt(b.updatedTime, 10),
    );

    const cumulativeByTraderState = traderLineConfigs.reduce<Record<string, number>>((acc, config) => {
      acc[config.userName] = 0;
      return acc;
    }, {});

    let cumulativePnL = 0;

    return sortedTrades.map((trade, index) => {
      const pnl = Number.parseFloat(trade.closedPnl || '0');
      cumulativePnL += pnl;
      const updatedTimestamp = Number.parseInt(trade.updatedTime, 10);
      const updatedDate = new Date(updatedTimestamp);
      const userName = trade.userName;

      if (!(userName in cumulativeByTraderState)) {
        cumulativeByTraderState[userName] = 0;
      }
      cumulativeByTraderState[userName] += pnl;

      return {
        label: format(updatedDate, 'MMM dd'),
        fullDate: format(updatedDate, 'dd/MM/yyyy HH:mm'),
        cumulativePnL,
        tradePnL: pnl,
        tradePnLBar: pnl,
        userName,
        symbol: trade.symbol,
        side: trade.side,
        quantity: Number.parseFloat(trade.qty || '0'),
        entryPrice: Number.parseFloat(trade.avgEntryPrice || '0'),
        exitPrice: Number.parseFloat(trade.avgExitPrice || '0'),
        tradeCount: index + 1,
        cumulativeByTrader: { ...cumulativeByTraderState },
      } satisfies TradeChartPoint;
    });
  }, [allClosedPnL, traderLineConfigs]);

  const symbolPerformance: SymbolPerformanceStat[] = useMemo(() => {
    const map = new Map<string, SymbolPerformanceStat>();

    allClosedPnL.forEach((trade) => {
      const symbol = trade.symbol || 'Unknown';
      const pnl = Number.parseFloat(trade.closedPnl || '0') || 0;
      const qty = Number.parseFloat(trade.qty || '0') || 0;
      const entryValue = Number.parseFloat(trade.cumEntryValue || '0');
      const fallbackVolume = qty * (Number.parseFloat(trade.avgEntryPrice || '0') || 0);
      const tradeVolume = Number.isFinite(entryValue) && entryValue > 0 ? entryValue : fallbackVolume;
      const holdHours =
        (Number.parseInt(trade.updatedTime, 10) - Number.parseInt(trade.createdTime, 10)) /
        (1000 * 60 * 60);

      const stats = map.get(symbol) ?? {
        symbol,
        totalPnl: 0,
        trades: 0,
        wins: 0,
        totalVolume: 0,
        totalQty: 0,
        totalHoldHours: 0,
        winRate: 0,
        avgPnl: 0,
        avgHoldHours: 0,
        avgSize: 0,
      };

      stats.totalPnl += pnl;
      stats.trades += 1;
      if (pnl > 0) {
        stats.wins += 1;
      }
      stats.totalVolume += Number.isFinite(tradeVolume) ? tradeVolume : 0;
      stats.totalQty += Number.isFinite(qty) ? qty : 0;
      stats.totalHoldHours += Number.isFinite(holdHours) && holdHours > 0 ? holdHours : 0;

      map.set(symbol, stats);
    });

    return Array.from(map.values()).map((stat) => ({
      ...stat,
      winRate: stat.trades > 0 ? (stat.wins / stat.trades) * 100 : 0,
      avgPnl: stat.trades > 0 ? stat.totalPnl / stat.trades : 0,
      avgHoldHours: stat.trades > 0 ? stat.totalHoldHours / stat.trades : 0,
      avgSize: stat.trades > 0 ? stat.totalQty / stat.trades : 0,
    }));
  }, [allClosedPnL]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  const handleExportToExcel = () => {
    if (!performanceData) return;

    exportTradingDataToExcel({
      users: performanceData.users,
      aggregate: performanceData.aggregate,
      dateRange,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          </div>
          <LoadingSkeleton />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            Error loading performance data: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </Layout>
    );
  }

  if (!performanceData) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
          <div className="p-4 bg-gray-50 text-gray-700 rounded-md">No performance data available</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Trading Performance</h1>
            <p className="text-sm text-gray-600 mt-1">Production trading data • Updated: {timestampLabel}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <DateRangePicker value={dateRange} onChange={handleDateRangeChange} className="w-full sm:w-auto" />
            <button
              onClick={() => refetch()}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              aria-label="Refresh performance data"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={handleExportToExcel}
              disabled={!performanceData}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              aria-label="Export trading performance to Excel"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>

        <AggregateMetricsSection aggregate={aggregate as AggregateMetrics} />
        <PerformanceChartSection data={chartData} traders={traderLineConfigs} />
        <CurrentPositionsSection positions={allPositions} />
        <TraderPerformanceSummary users={users as UserPerformanceData[]} />
        <TradingFeesSummary closedTrades={allClosedPnL} />
        <CoinPerformanceSummary symbols={symbolPerformance} />
        <ClosedTradesTable trades={allClosedPnL} />
        <TradeExecutionsTable trades={allTrades} enabled={SHOW_ALL_TRADE_EXECUTIONS} />

        {allClosedPnL.length === 0 && allTrades.length === 0 && allPositions.length === 0 && (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-gray-400 mb-4">No Trading Activity</div>
            <p className="text-gray-600">
              No trades or positions found in the selected period for users with production API keys.
            </p>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">{note}</div>
      </div>
    </Layout>
  );
};
